import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Investor, Contact } from "@/types/investor";
import InvestorProcessing from "@/components/investors/InvestorProcessing";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import InvestorsList from "@/components/InvestorsList";
import { RefreshCw, AlertCircle, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSaveInvestorData } from "@/hooks/investor/useSaveInvestorData";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getBatchProcessingState } from "@/utils/investors/processingUtils";
import { 
  checkSessionActivity, 
  checkForStaleState, 
  checkSyncActivity,
  markSyncCompleted,
  processingInvestors,
  failedInvestors
} from "@/utils/investors/processing/processingState";
import { getAPIKey, getAllStoredApiKeys } from "@/utils/ai/apiKeyUtils";
import APIKeyDebug from "@/components/debug/APIKeyDebug";
import { exportToExcel } from "@/utils/excelExport";

interface InvestorsPageProps {
  investors: Investor[];
  contacts: Contact[];
  businessInfo: string;
  relevanceContext: string;
  selectedInvestor: Investor | null;
  processedIds: Set<string>;
  isAnalyzing: boolean;
  isProcessingBatch: boolean;
  isEnriching: boolean;
  setSelectedInvestor: (investor: Investor | null) => void;
  setIsEnriching: (isEnriching: boolean) => void;
  forceReloadFromDatabase: () => Promise<boolean>;
  handleProcessInvestor: (investor: Investor) => Promise<Investor>;
  handleEnrichInvestor: (investor: Investor) => Promise<Investor>;
  handleProcessBatch: (batchSize: number) => Promise<Investor[]>; // Fixed: Changed from Promise<void> to Promise<Investor[]>
  handleInvestorUpdate: (investor: Investor) => void;
  isInvestorProcessed: (investor: Investor) => boolean;
  handleRelevanceSubmit: (context: string) => Promise<void>;
  setRelevanceContext: (context: string) => void;
  isLoadingData: boolean;
}

const InvestorsPage = ({
  investors,
  contacts,
  businessInfo,
  relevanceContext,
  selectedInvestor,
  processedIds,
  isAnalyzing,
  isProcessingBatch,
  isEnriching,
  setSelectedInvestor,
  setIsEnriching,
  forceReloadFromDatabase,
  handleProcessInvestor,
  handleEnrichInvestor,
  handleProcessBatch,
  handleInvestorUpdate,
  isInvestorProcessed,
  handleRelevanceSubmit,
  setRelevanceContext,
  isLoadingData
}: InvestorsPageProps) => {
  const navigate = useNavigate();
  const [staleStateDetected, setStaleStateDetected] = useState(false);
  const [activeSyncDetected, setActiveSyncDetected] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { pendingCount } = useSaveInvestorData();
  
  // Add page load timestamp for debugging
  const pageLoadTimestamp = useRef(new Date().toISOString());
  
  // State to track pending saves count
  const [pendingSaves, setPendingSaves] = useState(0);

  // Wrapper function to handle the return type mismatch
  const handleProcessBatchWrapper = async (batchSize: number): Promise<void> => {
    await handleProcessBatch(batchSize);
  };

  // Handle Excel export
  const handleExcelExport = async () => {
    if (investors.length === 0) {
      toast.error("No investor data to export");
      return;
    }

    try {
      setIsExporting(true);
      console.log(`Exporting ${investors.length} investors and ${contacts.length} contacts to Excel`);
      
      exportToExcel({
        investors,
        contacts
      }, 'investors_enriched_data');
      
      toast.success(`Successfully exported ${investors.length} investors to Excel`);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export data to Excel");
    } finally {
      setIsExporting(false);
    }
  };

  // Check for API keys on page load and log them
  useEffect(() => {
    console.log("InvestorsPage: Checking for API keys on mount");
    const openaiKey = getAPIKey('openai', '');
    const perplexityKey = getAPIKey('perplexity', '');
    
    console.log("API key availability on InvestorsPage:", {
      openai: !!openaiKey,
      perplexity: !!perplexityKey 
    });
    
    // Also log all possible API key locations for debugging
    getAllStoredApiKeys();
  }, []);

  // Check for previous batch state on mount for recovery
  useEffect(() => {
    // Log page mount
    console.log(`InvestorsPage mounted at ${pageLoadTimestamp.current}`);
    
    const checkForBatchRecovery = () => {
      console.log("Checking for batch recovery...");
      const batchProgress = localStorage.getItem("batch_progress");
      const batchCompletion = localStorage.getItem("batch_completion");
      
      if (batchProgress && !batchCompletion) {
        // We have a batch in progress without completion - might need recovery
        const progress = JSON.parse(batchProgress);
        const timeDiff = new Date().getTime() - new Date(progress.timestamp).getTime();
        
        console.log(`Found incomplete batch: ${JSON.stringify(progress)}, time diff: ${timeDiff}ms`);
        
        // If the last update was more than 2 minutes ago, we might need recovery
        if (timeDiff > 120000) {
          console.warn("Detected incomplete batch processing from a previous session");
        }
      } else {
        console.log("No incomplete batch found");
      }
    };
    
    // Also check for incomplete sync operations
    const checkForIncompleteSync = () => {
      const hasSyncActivity = checkSyncActivity();
      if (hasSyncActivity) {
        console.warn("Detected incomplete sync operation from previous session");
        setActiveSyncDetected(true);
        markSyncCompleted(); // Mark it complete since we're loading fresh data now
      }
    };
    
    // Run recovery checks after data is loaded
    if (investors.length > 0) {
      console.log(`Running recovery check with ${investors.length} investors`);
      checkForBatchRecovery();
      checkForIncompleteSync();
      
      // Debug info about current state
      console.log(`Current state: ${investors.length} investors, ${processedIds.size} processed IDs`);
      console.log(`Actively processing: ${processingInvestors.size} investors`);
      console.log(`Failed investors: ${failedInvestors.size}`);
    }
  }, [investors.length, processedIds.size]);

  // Check for stale state due to inactivity
  useEffect(() => {
    // First, check if there was a long period of inactivity
    const wasInactive = checkSessionActivity();
    
    // If inactive for a while, check if state might be stale
    if (wasInactive && investors.length > 0) {
      const stateIsPotentiallyStale = checkForStaleState();
      
      if (stateIsPotentiallyStale) {
        console.log("Detected potentially stale state after period of inactivity");
        setStaleStateDetected(true);
        toast.warning("Session was inactive for a while. Click 'Reload from Database' to ensure data is fresh.");
      }
    }
  }, [investors.length]);
  
  // Update pending saves count
  useEffect(() => {
    const interval = setInterval(() => {
      const pendingCountValue = pendingCount();
      setPendingSaves(pendingCountValue);
      
      if (pendingCountValue > 0) {
        console.log(`Pending saves: ${pendingCountValue}`);
      }
      
      // Check for active processing
      const processingState = getBatchProcessingState();
      if (processingState.activeProcessing) {
        console.log(`Active processing: ${processingState.processed} investors`);
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [pendingCount]);

  // Handler for selecting an investor and navigating to email generator
  const handleSelectInvestor = (investor: any) => {
    console.log(`Selected investor: ${investor.firm} (${investor.id})`);
    setSelectedInvestor(investor);
    navigate('/email-generator');
  };

  // Count processed investors using the isInvestorProcessed function
  const processedCount = investors.filter(isInvestorProcessed).length;
  console.log(`InvestorsPage render - Total investors: ${investors.length}, Processed: ${processedCount}, Pending saves: ${pendingSaves}`);

  const handleForceReload = async () => {
    try {
      console.log("Force reloading data from database...");
      toast.loading("Reloading data from database...");
      const success = await forceReloadFromDatabase();
      console.log(`Force reload complete, success: ${success}`);
      
      if (success) {
        toast.success("Data successfully reloaded from database");
        setStaleStateDetected(false);
        setActiveSyncDetected(false);
      } else {
        toast.warning("No data found in database or reload failed");
      }
    } catch (error) {
      console.error("Error reloading data:", error);
      toast.error("Failed to reload data from database");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Investors</h1>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={handleExcelExport}
            disabled={isExporting || investors.length === 0}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            {isExporting ? "Exporting..." : "Export to Excel"}
          </Button>
          <Button 
            variant={(staleStateDetected || activeSyncDetected) ? "default" : "outline"} 
            onClick={handleForceReload} 
            className={`flex items-center gap-2 ${(staleStateDetected || activeSyncDetected) ? "animate-pulse" : ""}`}
            disabled={isLoadingData}
          >
            <RefreshCw className="h-4 w-4" />
            Reload from Database
          </Button>
        </div>
      </div>
      
      {staleStateDetected && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Your session was inactive for a while and data may be stale. Click "Reload from Database" to ensure you have the latest data.
          </AlertDescription>
        </Alert>
      )}
      
      {activeSyncDetected && (
        <Alert variant="warning" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Detected an incomplete data sync operation. Click "Reload from Database" to ensure database consistency.
          </AlertDescription>
        </Alert>
      )}
      
      {investors.length > 0 && (
        <>
          <InvestorProcessing
            investors={investors}
            isAnalyzing={isAnalyzing}
            isProcessingBatch={isProcessingBatch}
            processedCount={processedCount}
            relevanceContext={relevanceContext}
            setRelevanceContext={setRelevanceContext}
            onRelevanceSubmit={handleRelevanceSubmit || (async () => Promise.resolve())}
            onProcessBatch={handleProcessBatchWrapper}
            isInvestorProcessed={isInvestorProcessed}
            pendingSaveCount={pendingSaves}
          />
          
          <Card>
            <CardHeader>
              <CardTitle>Investors</CardTitle>
              <CardDescription>
                {relevanceContext 
                  ? "Investors sorted by relevance to your criteria" 
                  : "Select an investor to generate an email template"}
                {pendingSaves > 0 && ` (${pendingSaves} updates pending)`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <InvestorsList 
                investors={investors} 
                contacts={contacts} 
                selectedInvestor={selectedInvestor}
                onSelectInvestor={handleSelectInvestor}
                relevanceContext={relevanceContext}
                onInvestorUpdate={handleInvestorUpdate}
                isEnriching={isEnriching}
                setIsEnriching={setIsEnriching}
                onProcessInvestor={handleProcessInvestor}
                onEnrichInvestor={handleEnrichInvestor}
                isInvestorProcessed={isInvestorProcessed}
              />
            </CardContent>
          </Card>
          
          {/* Add API key debug panel to the page */}
          <APIKeyDebug />
        </>
      )}
      
      {investors.length === 0 && (
        <div className="text-center py-12">
          <p className="text-lg text-muted-foreground">No investors data found</p>
          <p className="text-sm mt-2">Upload your investors data on the Upload Data page</p>
        </div>
      )}
    </div>
  );
};

export default InvestorsPage;
